/**
 * Driver trainings catalogue — extracted from hrSlice.trainDriver
 * (11/02/2026 — refactor hrSlice).
 *
 * Defines every training available in the HR module : driving licenses,
 * HSE refreshers (FCO, medical, CACES…), eco-conduite, stress management,
 * and specialty trainings (Messagerie, Frigo, BTP, ADR, Export, Vrac…).
 *
 * Each entry exposes :
 *   • name           — French label shown to the player
 *   • cost           — base cost in € (specialty trainings scale via
 *                      getSpecialtyCostMultiplier when the driver already
 *                      has 2+ specialties — handled in the slice).
 *   • days           — duration in working days (driver is `status: 'training'`
 *                      until trainingEndDay).
 *   • givePermis     — license unlocked on completion (e.g. 'C', 'CE',
 *                      'CE+exceptionnel').
 *   • requiresPermis — license prerequisite (blocks training otherwise).
 *   • giveSpecialty  — specialty unlocked on completion.
 *   • giveCertification — certification key pushed into driver.certifications.
 *   • boostCompetence — flat +X bonus to driver.competence (capped at 5).
 *   • boostSatisfaction — flat +X bonus (capped at 100).
 *   • reduceFatigue  — flat -X malus on driver.fatigue (floor 0).
 *
 * XP bonus granted on completion lives in `TRAINING_XP_BY_TYPE` below.
 */
export const TRAININGS_CATALOGUE = {
  permisC:        { name: 'Permis C', cost: 3500, days: 10, givePermis: 'C' },
  permisCE:       { name: 'Permis CE', cost: 5000, days: 15, givePermis: 'CE' },
  // 05/02/2026 — Habilitation « convoi exceptionnel » dédiée, prérequis CE.
  // Forme un chauffeur qualifié à conduire le tracteur Heavyhaul XXL
  // (transport éoliennes, fusée, maison préfab). Coût élevé (8 000 €,
  // 7 jours) pour matcher la rareté de la qualification.
  permisExceptional: { name: 'Habilitation Convoi exceptionnel', cost: 8000, days: 7, givePermis: 'CE+exceptionnel', requiresPermis: 'CE' },
  // 01/06/2026 — HSE compliance (Claire Maréchal). FCO = Formation Continue
  // Obligatoire, mandatory refresh every 5 years for pro drivers ≥ 3,5T.
  // No license/specialty grant — only resets the FCO validity anchor.
  // 02/06/2026 — Bumped 2→5 days (35h réglementaires en France).
  fco:            { name: 'FCO — Formation Continue Obligatoire', cost: 600, days: 5 },
  // 01/06/2026 — Phase 5 HSE refresher trainings (Claire Maréchal).
  // None grants license/specialty; they only reset the corresponding
  // HSE validity anchor consumed by helpers/hseCompliance.js.
  // 02/06/2026 — Médical reste 1 j (visite admin) ; CACES recyclage 2→3 j ;
  // éco-conduite 3→2 j (recyclage 14 h) ; gestion stress 2→3 j.
  medical:        { name: "Visite médicale d'aptitude", cost: 200, days: 1 },
  caces:          { name: 'CACES R489 / R372 (recyclage)', cost: 800, days: 3 },
  ecoConduite:    { name: 'Éco-conduite', cost: 800, days: 3, boostCompetence: 0.2 },
  gestionStress:  { name: 'Gestion du stress', cost: 600, days: 2, boostSatisfaction: 10, reduceFatigue: 5 },
  // Specialty trainings — grant a NEW specialty. Cap removed ; the cost
  // multiplier grows for slot 3+ (handled in the slice).
  // 04/02/2026 — Phase F1 : libellés alignés sur les 8 axes simplifiés.
  specMessagerie:       { name: 'Spécialisation Messagerie / Colis',  cost: 1800, days: 5,  giveSpecialty: 'Messagerie/Colis' },
  specFrigo:            { name: 'Spécialisation Frigo / Pharma',       cost: 3500, days: 10, giveSpecialty: 'Frigo/Pharma', boostCompetence: 0.3 },
  specBTP:              { name: 'Spécialisation BTP / Matériaux / Engins TP', cost: 3200, days: 10, giveSpecialty: 'BTP/Matériaux/Engins TP', boostCompetence: 0.3 },
  specADR:              { name: 'Spécialisation ADR / Produits sensibles', cost: 4500, days: 12, giveSpecialty: 'ADR/Produits sensibles', boostCompetence: 0.4 },
  specExport:           { name: 'Spécialisation Export UE',            cost: 4000, days: 10, giveSpecialty: 'Export UE', boostCompetence: 0.3 },
  specVrac:             { name: 'Spécialisation Vrac / Palettes',      cost: 2200, days: 5,  giveSpecialty: 'Vrac/Palettes', boostCompetence: 0.2 },
  // 05/02/2026 — Q1 retour user prod : formation `specTransportAnimaux`
  // (bétaillère) supprimée du catalogue.
  // 04/02/2026 — Sensibilisations Phase F1. Pas de spécialité accordée,
  // mais ajoutent une "certification" (zfe ou rse) côté driver.
  sensZFE: { name: 'ZFE / Technologie', cost: 900, days: 5, giveCertification: 'zfe' },
  sensRSE: { name: 'Image RSE / Marchés publics', cost: 1200, days: 5, giveCertification: 'rse' },
};

/**
 * XP bonus granted on training completion. Specialty trainings give the
 * highest boosts ; admin/refresher trainings ~10-25 XP.
 */
export const TRAINING_XP_BY_TYPE = {
  permisC: 40, permisCE: 60, fco: 10,
  // 05/02/2026 — Habilitation exceptionnelle = qualification rare, gros boost.
  permisExceptional: 80,
  ecoConduite: 15, gestionStress: 15,
  sensZFE: 25, sensRSE: 25,
  specMessagerie: 40, specFrigo: 70, specBTP: 65, specADR: 80,
  specExport: 70, specVrac: 45,
  // 05/02/2026 — Q1 retour user prod : specTransportAnimaux retiré.
};
